<template>
  <div class="cp-editor">
    <!-- Header -->
    <div class="cp-editor__bar">
      <h2 class="cp-editor__title">
        {{ localId
          ? ((options.Lang && options.Lang.editCheckoutProfile) || 'Edit checkout profile')
          : ((options.Lang && options.Lang.newCheckoutProfile) || 'New checkout profile') }}
      </h2>
      <div class="cp-editor__bar-actions">
        <md-button class="md-raised" @click="close()">
          {{ (options.Lang && options.Lang.Fenster_schließen) || 'Close' }}
        </md-button>
        <md-button class="md-raised md-primary" @click="save" :disabled="saving">
          {{ (options.Lang && options.Lang.Speichern) || 'Save' }}
        </md-button>
      </div>
    </div>

    <div v-if="loaded" class="cp-editor__body">
      <!-- Profile name -->
      <div class="cp-card cp-card--name">
        <CustomInput
            :label="(options.Lang && options.Lang.profileName) || 'Profile name'"
            v-model="profile.name"
            :placeholder="(options.Lang && options.Lang.profileNamePlaceholder) || 'e.g. Boomerang Standard'" />
      </div>

      <!-- Global controls: checkout-step tabs + a separate Brandings pill (not a
           step), with the per-language switch on the far right for text steps. -->
      <div class="cp-controls">
        <div class="cp-tab-groups">
          <SegmentedTabs :value="activeStep" :tabs="stepTabs" @input="activeStep = $event" />
          <span class="cp-tab-sep" aria-hidden="true"></span>
          <SegmentedTabs :value="activeStep" :tabs="brandingTabs" @input="activeStep = $event" />
        </div>
        <div v-if="activeStepHasLang" class="cp-lang-switch">
          <LanguagePills
              :all="langs"
              :active="activeLangs"
              :value="activeLang"
              :langLabels="langLabelMap"
              :addLabel="(options.Lang && options.Lang.add_lang) || 'Add language'"
              :showDefault="true"
              :defaultLang="profile.default_lang"
              :defaultLabel="options.Lang && options.Lang.defaultLanguage"
              :setDefaultLabel="options.Lang && options.Lang.setDefaultLanguage"
              @input="activeLang = $event"
              @add="addLang"
              @remove="removeLang"
              @set-default="setDefaultLang" />
        </div>
      </div>

      <!-- Activity appears in the tab bar but is disabled (no profile-level text
           yet), so it is never the active step. -->

      <!-- ===== STEP: Trip Overview / Reiseübersicht (summary sidebar boxes) ===== -->
      <StepTabOverview
          v-if="activeStep === 'overview'"
          :options="options"
          :profile="profile"
          :lang="activeLang"
          :brandColor="brandColor" />

      <!-- ===== STEP: Contact (mobile-required toggle + per-field notes) ===== -->
      <template v-else-if="activeStep === 'contact'">
        <template v-for="lng in activeLangs">
          <StepTabContact
              v-show="activeLang === lng"
              :key="'contact_' + lng"
              :options="options"
              :profile="profile"
              :text="texts[lng]"
              :noteFields="contactNoteFields"
              :lang="lng"
              :brandColor="brandColor"
              @add-note-field="addContactNoteField"
              @remove-note-field="removeContactNoteField"
              @change-note-field="changeContactNoteField" />
        </template>
      </template>

      <!-- ===== STEP: Travelers (name-disclaimer box visibility) ===== -->
      <template v-else-if="activeStep === 'travelers'">
        <template v-for="lng in activeLangs">
          <StepTabTravelers
              v-show="activeLang === lng"
              :key="'travelers_' + lng"
              :options="options"
              :profile="profile"
              :text="texts[lng]"
              :richHtml="richHtml[lng] || {}"
              :lang="lng"
              :brandColor="brandColor" />
        </template>
      </template>

      <!-- ===== STEP: Options (insurance-confirm box visibility) ===== -->
      <template v-else-if="activeStep === 'options'">
        <template v-for="lng in activeLangs">
          <StepTabOptions
              v-show="activeLang === lng"
              :key="'options_' + lng"
              :options="options"
              :profile="profile"
              :text="texts[lng]"
              :richHtml="richHtml[lng] || {}"
              :lang="lng"
              :brandColor="brandColor" />
        </template>
      </template>

      <!-- ===== STEP: Questions & Remarks ===== -->
      <StepTabQuestions
          v-else-if="activeStep === 'custom'"
          :options="options"
          :questions="questions"
          :text="texts[activeLang]"
          :lang="activeLang"
          :brandColor="brandColor"
          @add-box="addBox"
          @remove-box="removeBox"
          @move-box="moveBox"
          @add-element="addElement"
          @remove-element="removeElement"
          @move-element="moveElement"
          @change-element-type="changeElementType"
          @add-option="addOption"
          @remove-option="removeOption" />

      <!-- ===== STEP: Terms ===== -->
      <!-- Per-language: keep all language editors mounted (v-show) so tiptap
           instances aren't destroyed/rebuilt on every language switch. -->
      <template v-else-if="activeStep === 'terms'">
        <template v-for="lng in activeLangs">
          <StepTabTerms
              v-show="activeLang === lng"
              :key="'terms_' + lng"
              :options="options"
              :profile="profile"
              :text="texts[lng]"
              :editors="editors[lng]"
              :richFields="termsRichFields"
              :richHtml="richHtml[lng] || {}"
              :lang="lng"
              :brandColor="brandColor" />
        </template>
      </template>

      <!-- ===== STEP: Payment ===== -->
      <template v-else-if="activeStep === 'payment'">
        <template v-for="lng in activeLangs">
          <StepTabPayment
              v-show="activeLang === lng"
              :key="'payment_' + lng"
              :options="options"
              :profile="profile"
              :text="texts[lng]"
              :editor="editors[lng] && editors[lng].checkout_payment_confirm"
              :richHtml="richHtml[lng] || {}"
              :lang="lng"
              :brandColor="brandColor"
              :profileId="localId"
              :stripeStatus="stripeStatus"
              :stripeEnabledGlobal="stripeEnabledGlobal"
              @stripe-changed="onStripeChanged" />
        </template>
      </template>

      <!-- ===== STEP: Confirmation (reservation-email recipients + PDF toggles) ===== -->
      <StepTabConfirmation
          v-else-if="activeStep === 'confirmation'"
          :options="options"
          :profile="profile"
          :lang="activeLang"
          :brandColor="brandColor" />

      <!-- ===== STEP: PDF (reservation PDF settings + live preview) ===== -->
      <StepTabPdf
          v-else-if="activeStep === 'pdf'"
          :options="options"
          :text="texts[activeLang]"
          :lang="activeLang"
          :brandColor="brandColor" />


      <!-- ===== STEP: Brandings (config, no preview) ===== -->
      <div v-else-if="activeStep === 'brandings'" class="cp-section">
        <div class="cp-card">
          <p class="cp-hint">
            {{ (options.Lang && options.Lang.brandingsAttachHint) || 'Select which brandings use this checkout profile. A branding can only use one profile at a time — ticking it here moves it off any other profile.' }}
          </p>
          <p v-if="!allBrandings.length" class="cp-empty">
            {{ (options.Lang && options.Lang.loadingBrandings) || 'Loading brandings…' }}
          </p>
          <ul class="cp-brand-list">
            <li v-for="b in allBrandings" :key="b.id" class="cp-brand-item">
              <span class="cp-brand-name">{{ b.name }}</span>
              <span
                  v-if="b.checkout_profile_id && Number(b.checkout_profile_id) !== Number(id) && !selectedBrandingIds.includes(b.id)"
                  class="cp-brand-taken">
                {{ (options.Lang && options.Lang.usedByAnotherProfile) || 'used by another profile' }}
              </span>
              <CustomSwitch
                  class="cp-brand-switch"
                  :value="selectedBrandingIds.includes(b.id)"
                  @input="toggleBranding(b.id)" />
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-else class="cp-loading">
      <md-progress-spinner md-mode="indeterminate" :md-diameter="40" :md-stroke="3" />
    </div>

    <!-- Save feedback — stays in place, no redirect. -->
    <Toast
        :show.sync="toast.show"
        :message="toast.message"
        :type="toast.type"
        :closeLabel="(options.Lang && options.Lang.Fenster_schließen) || 'Close'" />
  </div>
</template>

<script>
import { createRichEditor } from '../../utils/editorFactory'
import SegmentedTabs from '../parts/SegmentedTabs.vue'
import LanguagePills from '../parts/LanguagePills.vue'
import CustomInput from '../parts/CustomInput.vue'
import CustomSwitch from '../parts/CustomSwitch.vue'
import Toast from '../parts/Toast.vue'
import StepTabQuestions from '../extraFeatures/checkoutSteps/StepTabQuestions.vue'
import StepTabTerms from '../extraFeatures/checkoutSteps/StepTabTerms.vue'
import StepTabPayment from '../extraFeatures/checkoutSteps/StepTabPayment.vue'
import StepTabConfirmation from '../extraFeatures/checkoutSteps/StepTabConfirmation.vue'
import StepTabPdf from '../extraFeatures/checkoutSteps/StepTabPdf.vue'
import StepTabTravelers from '../extraFeatures/checkoutSteps/StepTabTravelers.vue'
import StepTabOptions from '../extraFeatures/checkoutSteps/StepTabOptions.vue'
import StepTabOverview from '../extraFeatures/checkoutSteps/StepTabOverview.vue'
import StepTabContact from '../extraFeatures/checkoutSteps/StepTabContact.vue'
import { CONTACT_NOTE_FIELDS, contactNoteKey } from '../extraFeatures/checkoutSteps/contactNoteFields'

const DEFAULT_LANGS = ['de', 'en'];

const LANGS = ['de', 'en', 'fr', 'nl', 'el'];
// Long-form fields stored as HTML, edited with a rich editor (one per language).
const RICH_FIELDS = [
  { key: 'checkout_insurance_confirm', langKey: 'checkout_text_insurance_confirm', fallback: 'Insurance confirmation text' },
  { key: 'checkout_payment_confirm', langKey: 'checkout_text_payment_confirm', fallback: 'Payment confirmation text' },
  { key: 'checkout_name_text', langKey: 'checkout_text_name_text', fallback: 'Name disclaimer text' },
  { key: 'checkout_agb_confirm', langKey: 'checkout_text_agb_confirm', fallback: 'Terms consent text' },
  // NOTE: reservation_email_body is intentionally NOT here — it's a BRANDING field
  // owned by the seller reservation email; the profile must not carry/override it.
];
// Short fields stored as plain strings, edited with inputs.
const SHORT_FIELDS = [
  'checkout_payment_label', 'checkout_agb_heading', 'checkout_name_heading', 'checkout_name_confirm',
  'checkout_agb_link', 'agbs', 'checkout_datenschutz_link', 'datenschutz',
  'checkout_formblatt_link', 'formblatt_pdf', 'checkout_insolvenz_link', 'insolvenz_pdf',
  // Questions & Remarks step heading (per language; blank → buyer-step default).
  'checkout_custom_questions_title', 'checkout_custom_questions_subtitle',
  // Reservation PDF heading + intro (per language; blank → default localized title).
  'pdf_title', 'pdf_description',
  // Contact-step per-field notes (contact_note_<field>, Contact tab).
  ...CONTACT_NOTE_FIELDS.map(f => contactNoteKey(f.key)),
];

// Which rich fields belong to the Terms step (the rest are owned by other step tabs).
const TERMS_RICH_KEYS = ['checkout_insurance_confirm', 'checkout_name_text', 'checkout_agb_confirm'];

// Questions & Remarks "box" model. Each box is a composable container holding an
// ordered list of elements the admin adds freely. The backend stores the whole box
// (elements + all per-language text) as JSON in options_json (no schema change).
// Element types: title | textarea | input | single | multiple | checkbox.
const ELEMENT_TYPES = ['title', 'textarea', 'input', 'single', 'multiple', 'checkbox'];

// Checkout-step tabs, in the buyer's flow order. `lang` flags whether the step
// edits per-language text (and thus shows the language switch); `placeholder`
// flags steps with no profile text yet (Contact/Travelers/Activity/Options).
// Questions & Remarks sits late in the flow (just before Payment), per product.
const STEP_DEFS = [
  { value: 'contact', labelKey: 'checkout_step_contact', fallback: 'Contact', icon: 'person_outline', lang: true },
  { value: 'travelers', labelKey: 'checkout_step_travelers', fallback: 'Travelers', icon: 'groups', lang: true },
  { value: 'activity', labelKey: 'checkout_step_questions', fallback: 'Activity', icon: 'local_activity', placeholder: true },
  { value: 'options', labelKey: 'checkout_step_options', fallback: 'Options', icon: 'tune', lang: true },
  { value: 'custom', labelKey: 'checkout_step_custom_questions', fallback: 'Questions & Remarks', icon: 'help_outline', lang: true },
  { value: 'terms', labelKey: 'checkout_step_terms', fallback: 'Terms', icon: 'gavel', lang: true },
  { value: 'payment', labelKey: 'checkout_step_payment', fallback: 'Payment', icon: 'payments', lang: true },
  { value: 'confirmation', labelKey: 'checkout_step_confirmation', fallback: 'Confirmation', icon: 'check_circle', lang: true },
  // Not a buyer step — the trip-summary sidebar shown beside every step.
  { value: 'overview', labelKey: 'checkout_step_overview', fallback: 'Trip Overview', icon: 'receipt_long', lang: true },
  // Not a buyer step — the reservation PDF settings + a live preview, last in the bar.
  { value: 'pdf', labelKey: 'checkout_step_pdf', fallback: 'PDF', icon: 'picture_as_pdf', lang: true },
];

// Brandings is NOT a checkout step (it's profile↔branding attachment config), so
// it lives in its own pill tab beside the step group rather than inside it.
const BRANDINGS_TAB = { value: 'brandings', labelKey: 'section_brandings', fallback: 'Brandings', icon: 'apartment' };

export default {
  name: 'editCheckoutProfile',
  components: {
    SegmentedTabs, LanguagePills, CustomInput, CustomSwitch, Toast,
    StepTabQuestions, StepTabTerms, StepTabPayment, StepTabConfirmation, StepTabPdf, StepTabTravelers, StepTabOptions, StepTabOverview, StepTabContact
  },
  props: {
    options: { type: Object, required: true },
    id: { type: [Number, String], default: null },
  },
  data() {
    return {
      langs: LANGS,
      termsRichFields: RICH_FIELDS.filter(rt => TERMS_RICH_KEYS.includes(rt.key)),
      loaded: false,
      saving: false,
      // Mutable id: starts from the `id` prop; set after a create so a brand-new
      // profile flips into edit mode (the editor stays open after saving).
      localId: this.id,
      toast: { show: false, message: '', type: 'success' },
      activeStep: 'custom',
      activeLang: 'de',
      activeLangs: [...DEFAULT_LANGS],
      profile: {
        name: '',
        default_lang: 'de',
        reservation_email_1: '',
        reservation_email_2: '',
        send_pdf_to_buyer: false,
        allow_pdf_download: false,
        // Per-box checkout visibility. Defaults: all OFF except passolution (entry
        // requirements), matching a fresh install's default Nezasa-style layout.
        box_visibility: {
          name_disclaimer: false,
          insurance_confirm: false,
          custom_terms: false,
          passolution: true,
          payment_schedule: false,
          payment_confirm: false,
          stripe_enabled: false,
          summary_deposit: true,
          contact_mobile_required: false,
          email_salesman: false,
        },
      },
      // Contact-step fields that currently have a note row (shared across languages;
      // the note text itself is per language in texts[lang].contact_note_<field>).
      contactNoteFields: [],
      // Masked Stripe-key preview for the loaded profile: { exists, type, last4 }.
      // The plaintext key never reaches the CRM; this only drives the UI state.
      stripeStatus: { exists: false, type: null, last4: null },
      texts: this.blankTexts(),
      editors: {},   // { lang: { fieldKey: Editor } }
      // Live snapshot of every rich editor's HTML, keyed { lang: { fieldKey: html } }.
      // Updated on each editor 'update' so previews re-render as the user types.
      richHtml: {},
      questions: [],
      allBrandings: [],
      selectedBrandingIds: [],
    };
  },
  created() {
    this.loadBrandings();
    if (this.id) {
      this.loadProfile();
    } else {
      this.initEditors({});
      this.loaded = true;
    }
  },
  beforeDestroy() {
    this.destroyEditors();
  },
  computed: {
    brandColor() {
      return (this.options && this.options.globalinfo && this.options.globalinfo.color) || '#1a73e8';
    },
    // Global "Enable Stripe" flag from SuperAdmin (exposed via options.GlobalSettings).
    // When off, the per-profile Stripe toggle is shown disabled ("coming soon").
    stripeEnabledGlobal() {
      const gs = this.options && this.options.GlobalSettings;
      return !!(gs && (gs.stripe_enabled_global === 1 || gs.stripe_enabled_global === '1' || gs.stripe_enabled_global === true));
    },
    stepTabs() {
      const L = this.options.Lang || {};
      // Placeholder steps (no profile text yet) are shown but disabled so the
      // editor still mirrors the full buyer flow without offering empty tabs.
      return STEP_DEFS.map(s => ({
        value: s.value,
        label: L[s.labelKey] || s.fallback,
        icon: s.icon,
        disabled: !!s.placeholder,
        disabledHint: s.placeholder
          ? (L.stepNoEditableText || 'This step has no profile-level text to configure yet.')
          : ''
      }));
    },
    // Standalone Brandings pill (separate group from the step tabs).
    brandingTabs() {
      const L = this.options.Lang || {};
      return [{
        value: BRANDINGS_TAB.value,
        label: L[BRANDINGS_TAB.labelKey] || BRANDINGS_TAB.fallback,
        icon: BRANDINGS_TAB.icon
      }];
    },
    currentStepDef() {
      return STEP_DEFS.find(s => s.value === this.activeStep) || STEP_DEFS[0];
    },
    activeStepHasLang() {
      return !!this.currentStepDef.lang;
    },
    langLabelMap() {
      const map = {};
      this.langs.forEach(l => { map[l] = this.langLabel(l); });
      return map;
    }
  },
  methods: {
    langLabel(lang) {
      return (this.options.Lang && this.options.Lang.lang_code && this.options.Lang.lang_code[lang]) || lang.toUpperCase();
    },
    blankTexts() {
      const t = {};
      LANGS.forEach(l => {
        t[l] = {};
        SHORT_FIELDS.forEach(f => { t[l][f] = ''; });
      });
      return t;
    },
    /** Build one rich editor per (active language, rich field), seeded from stored texts. */
    initEditors(languagesData) {
      this.destroyEditors();
      const editors = {};
      const richHtml = {};
      this.activeLangs.forEach(lang => {
        editors[lang] = this.buildLangEditors(lang, languagesData && languagesData[lang], richHtml);
      });
      this.editors = editors;
      this.richHtml = richHtml;
    },
    /** Seed a single language's short fields + build its rich editors (+ html snapshot). */
    buildLangEditors(lang, langTexts, richHtmlTarget) {
      const t = langTexts || {};
      SHORT_FIELDS.forEach(f => {
        this.$set(this.texts[lang], f, t[f] || '');
      });
      const ed = {};
      const htmlSnap = {};
      RICH_FIELDS.forEach(rt => {
        const initial = t[rt.key] || '';
        htmlSnap[rt.key] = initial;
        // Keep the live HTML snapshot in sync so previews update while typing.
        ed[rt.key] = createRichEditor(initial, {
          onUpdate: ({ editor }) => this.syncRichHtml(lang, rt.key, editor.getHTML()),
        });
      });
      // Register the snapshot for this language (used by previews).
      if (richHtmlTarget) {
        richHtmlTarget[lang] = htmlSnap;
      } else {
        this.$set(this.richHtml, lang, htmlSnap);
      }
      return ed;
    },
    /** Update the reactive live-HTML snapshot for one rich field. */
    syncRichHtml(lang, key, html) {
      if (!this.richHtml[lang]) this.$set(this.richHtml, lang, {});
      this.$set(this.richHtml[lang], key, html);
    },
    /** Add a language: make it active, build its editors, and switch to it. */
    addLang(lang) {
      if (this.activeLangs.includes(lang)) return;
      this.activeLangs.push(lang);
      if (!this.texts[lang]) this.$set(this.texts, lang, {});
      this.$set(this.editors, lang, this.buildLangEditors(lang, null, null));
      this.activeLang = lang;
    },
    /** Add a note row for the first contact field that doesn't have one yet. */
    addContactNoteField() {
      const next = CONTACT_NOTE_FIELDS.find(f => !this.contactNoteFields.includes(f.key));
      if (next) this.contactNoteFields.push(next.key);
    },
    /** Remove a field's note row and blank its text in every language. */
    removeContactNoteField(field) {
      const i = this.contactNoteFields.indexOf(field);
      if (i !== -1) this.contactNoteFields.splice(i, 1);
      this.activeLangs.forEach(l => {
        if (this.texts[l]) this.$set(this.texts[l], contactNoteKey(field), '');
      });
    },
    /** Re-point a note row at another field, carrying each language's text over. */
    changeContactNoteField(oldField, newField) {
      if (oldField === newField || this.contactNoteFields.includes(newField)) return;
      const i = this.contactNoteFields.indexOf(oldField);
      if (i === -1) return;
      this.$set(this.contactNoteFields, i, newField);
      this.activeLangs.forEach(l => {
        const t = this.texts[l];
        if (!t) return;
        this.$set(t, contactNoteKey(newField), t[contactNoteKey(oldField)] || '');
        this.$set(t, contactNoteKey(oldField), '');
      });
    },
    /** Mark a language as the profile's default (fallback) language. */
    setDefaultLang(lang) {
      if (!this.activeLangs.includes(lang)) return;
      this.profile.default_lang = lang;
    },
    /** Remove a (non-default) language and its editors. */
    removeLang(lang) {
      if (DEFAULT_LANGS.includes(lang) || lang === this.profile.default_lang) return;
      const idx = this.activeLangs.indexOf(lang);
      if (idx === -1) return;
      this.activeLangs.splice(idx, 1);
      const langEditors = this.editors[lang] || {};
      Object.keys(langEditors).forEach(k => { if (langEditors[k] && langEditors[k].destroy) langEditors[k].destroy(); });
      this.$delete(this.editors, lang);
      this.$delete(this.richHtml, lang);
      if (this.activeLang === lang) this.activeLang = this.activeLangs[0] || 'de';
    },
    destroyEditors() {
      Object.keys(this.editors || {}).forEach(lang => {
        Object.keys(this.editors[lang] || {}).forEach(key => {
          const ed = this.editors[lang][key];
          if (ed && ed.destroy) ed.destroy();
        });
      });
      this.editors = {};
      this.richHtml = {};
    },
    /** Toggle a branding's presence in the selected set (CustomSwitch is boolean). */
    toggleBranding(id) {
      const idx = this.selectedBrandingIds.indexOf(id);
      if (idx === -1) this.selectedBrandingIds.push(id);
      else this.selectedBrandingIds.splice(idx, 1);
    },
    loadBrandings() {
      const self = this;
      self.doPost({}, 'checkout-profiles/brandings', function (res) {
        if (res.data.status !== 'error') {
          self.allBrandings = res.data.data || [];
          // Pre-tick brandings already attached to this profile.
          if (self.id) {
            self.selectedBrandingIds = self.allBrandings
              .filter(b => Number(b.checkout_profile_id) === Number(self.id))
              .map(b => b.id);
          }
        }
      });
    },
    /** Apply a Stripe key change reported by the payment tab (saved or disabled).
     *  The backend already persisted it; we just mirror the masked status + the
     *  stripe_enabled flag locally so the UI/preview stay in sync without a reload. */
    onStripeChanged(meta) {
      this.stripeStatus = {
        exists: !!(meta && meta.exists),
        type: (meta && meta.type) || null,
        last4: (meta && meta.last4) || null,
      };
      this.$set(this.profile.box_visibility, 'stripe_enabled', !!this.stripeStatus.exists);
    },
    loadProfile() {
      const self = this;
      self.doPost({ data: { id: self.id } }, 'checkout-profiles/get', function (res) {
        if (res.data.status === 'error') {
          self.showToast((self.options.Lang && self.options.Lang.profileLoadFailed), 'error');
          return;
        }
        const p = res.data.data || {};
        const bv = p.box_visibility || {};
        self.profile = {
          name: p.name || '',
          default_lang: LANGS.includes(p.default_lang) ? p.default_lang : 'de',
          reservation_email_1: p.reservation_email_1 || '',
          reservation_email_2: p.reservation_email_2 || '',
          send_pdf_to_buyer: !!p.send_pdf_to_buyer,
          allow_pdf_download: !!p.allow_pdf_download,
          box_visibility: {
            name_disclaimer: !!bv.name_disclaimer,
            insurance_confirm: !!bv.insurance_confirm,
            custom_terms: !!bv.custom_terms,
            // Default ON when the key is absent (legacy/new profiles).
            passolution: bv.passolution === undefined ? true : !!bv.passolution,
            payment_schedule: !!bv.payment_schedule,
            payment_confirm: !!bv.payment_confirm,
            stripe_enabled: !!bv.stripe_enabled,
            // Default ON when the key is absent (legacy/new profiles).
            summary_deposit: bv.summary_deposit === undefined ? true : !!bv.summary_deposit,
            contact_mobile_required: !!bv.contact_mobile_required,
            email_salesman: !!bv.email_salesman,
          },
        };
        // Masked Stripe key preview (never the key itself) — drives the toggle/modal UI.
        self.stripeStatus = {
          exists: !!(p.stripe && p.stripe.exists),
          type: (p.stripe && p.stripe.type) || null,
          last4: (p.stripe && p.stripe.last4) || null,
        };
        self.texts = self.blankTexts();
        // Active languages = defaults + any language the profile already has texts for.
        const present = Object.keys(p.languages || {});
        self.activeLangs = DEFAULT_LANGS.concat(present.filter(l => LANGS.includes(l) && !DEFAULT_LANGS.includes(l)));
        if (!self.activeLangs.includes(self.activeLang)) self.activeLang = self.activeLangs[0] || 'de';
        self.initEditors(p.languages || {});
        // A field has a note row when its text is non-empty in ANY language.
        self.contactNoteFields = CONTACT_NOTE_FIELDS
          .map(f => f.key)
          .filter(key => Object.values(p.languages || {}).some(
            t => t && String(t[contactNoteKey(key)] || '').trim() !== ''));
        self.questions = (p.questions || []).map(q => self.hydrateQuestion(q));
        self.loaded = true;
      });
    },
    /** Normalize a stored condition's trigger values to an array (back-compat with
     *  the legacy single `optionValue`). */
    normCondVals(cond) {
      if (Array.isArray(cond.optionValues)) return cond.optionValues.map(String).filter(v => v !== '');
      if (cond.optionValue != null && cond.optionValue !== '') return [String(cond.optionValue)];
      return [];
    },
    /** A per-language string map seeded from a stored labels object. */
    seedLangMap(src) {
      const m = {};
      LANGS.forEach(l => { m[l] = (src && src[l]) || ''; });
      return m;
    },
    uid() {
      // Avoids crypto dependency; sufficient for v-for keys within the dialog lifetime.
      return 'q_' + Math.floor(Math.random() * 1e9) + '_' + (this.uidSeq = (this.uidSeq || 0) + 1);
    },
    /** Normalize a stored element (from the server) into the editable UI shape. */
    hydrateElement(el) {
      const type = ELEMENT_TYPES.includes(el.type) ? el.type : 'title';
      // Stable id: keep the stored id, else mint one so conditions can reference it.
      const id = el.id || ('e' + Math.floor(Math.random() * 1e9));
      const condition = (el.condition && el.condition.elementId)
        ? { elementId: String(el.condition.elementId), optionValues: this.normCondVals(el.condition) }
        : null;
      const base = { uid: this.uid(), id, type, mainLabel: !!el.mainLabel, condition, labels: this.seedLangMap(el.labels) };
      if (type === 'input') {
        return { ...base, inputType: el.inputType === 'money' ? 'money' : 'text', includeInSummary: !!el.includeInSummary, required: !!el.required, minAmount: el.minAmount != null ? el.minAmount : '', charge: el.charge === 'per_traveler' ? 'per_traveler' : 'one_time' };
      }
      if (type === 'single' || type === 'multiple') {
        return { ...base, required: !!el.required, options: (el.options || []).map(o => ({ uid: this.uid(), value: o.value || '', labels: this.seedLangMap(o.labels) })) };
      }
      if (type === 'checkbox') {
        return { ...base, required: !!el.required };
      }
      return base; // title / textarea
    },
    /** Normalize a server box into the editable UI shape (ordered elements). */
    hydrateQuestion(box) {
      return { uid: this.uid(), name: box.name || '', elements: (box.elements || []).map(el => this.hydrateElement(el)) };
    },
    /** A fresh element of the given type. */
    blankElement(type) {
      // Mint a stable id up front so other elements can reference it in a condition.
      const base = { uid: this.uid(), id: 'e' + Math.floor(Math.random() * 1e9), type, mainLabel: false, condition: null, labels: this.seedLangMap() };
      if (type === 'input') return { ...base, inputType: 'text', includeInSummary: false, required: false, minAmount: '', charge: 'one_time' };
      if (type === 'single' || type === 'multiple') return { ...base, required: false, options: [] };
      if (type === 'checkbox') return { ...base, required: false };
      return base; // title / textarea
    },
    addBox() {
      // Blank name = use the default "Box N" display; the user can rename if they want.
      this.questions.push({ uid: this.uid(), name: '', elements: [] });
    },
    removeBox(index) {
      this.questions.splice(index, 1);
    },
    moveBox(index, dir) {
      const target = index + dir;
      if (target < 0 || target >= this.questions.length) return;
      const arr = this.questions;
      const tmp = arr[index];
      this.$set(arr, index, arr[target]);
      this.$set(arr, target, tmp);
    },
    addElement(box, type) {
      if (!ELEMENT_TYPES.includes(type)) return;
      box.elements.push(this.blankElement(type));
    },
    removeElement(box, index) {
      box.elements.splice(index, 1);
    },
    moveElement(box, index, dir) {
      const target = index + dir;
      if (target < 0 || target >= box.elements.length) return;
      const tmp = box.elements[index];
      this.$set(box.elements, index, box.elements[target]);
      this.$set(box.elements, target, tmp);
    },
    /** Switch a choice element between single/multiple in place (keeps options). */
    changeElementType(el, type) {
      if ((type !== 'single' && type !== 'multiple') || el.type === type) return;
      this.$set(el, 'type', type);
    },
    addOption(el) {
      if (!Array.isArray(el.options)) this.$set(el, 'options', []);
      el.options.push({ uid: this.uid(), value: '', labels: this.seedLangMap() });
    },
    removeOption(el, index) {
      el.options.splice(index, 1);
    },
    /** Assemble the per-language texts payload — only for languages the user added. */
    collectLanguages() {
      const languages = {};
      this.activeLangs.forEach(lang => {
        const out = {};
        SHORT_FIELDS.forEach(f => { out[f] = (this.texts[lang] && this.texts[lang][f]) || null; });
        RICH_FIELDS.forEach(rt => {
          const ed = this.editors[lang] && this.editors[lang][rt.key];
          out[rt.key] = ed ? ed.getHTML() : null;
        });
        languages[lang] = out;
      });
      return languages;
    },
    collectQuestions() {
      // Send each box's name + ordered elements; the backend stores them in options_json.
      return this.questions.map(box => ({
        name: (box.name || '').trim(),
        elements: (box.elements || []).map(el => {
          const out = { id: el.id || el.uid, type: el.type, mainLabel: el.type !== 'textarea' ? !!el.mainLabel : false, labels: el.labels };
          // Show-condition (any element): { elementId, optionValue } or null.
          out.condition = (el.condition && el.condition.elementId)
            ? { elementId: el.condition.elementId, optionValues: this.normCondVals(el.condition) }
            : null;
          if (el.type === 'input') {
            out.inputType = el.inputType === 'money' ? 'money' : 'text';
            out.includeInSummary = el.inputType === 'money' ? !!el.includeInSummary : false;
            out.required = !!el.required;
            out.minAmount = (el.inputType === 'money' && el.minAmount !== '' && el.minAmount != null && !isNaN(Number(el.minAmount))) ? Number(el.minAmount) : null;
            out.charge = (el.inputType === 'money' && el.charge === 'per_traveler') ? 'per_traveler' : 'one_time';
          } else if (el.type === 'single' || el.type === 'multiple') {
            out.required = !!el.required;
            out.options = (el.options || []).map(o => ({ value: o.value, labels: o.labels }));
          } else if (el.type === 'checkbox') {
            out.required = !!el.required;
          }
          return out;
        }),
      }));
    },
    /** Show an auto-dismissing toast (success/error) without leaving the editor. */
    showToast(message, type = 'success') {
      // Re-trigger cleanly even if a toast is already visible.
      this.toast.show = false;
      this.$nextTick(() => {
        this.toast.message = message;
        this.toast.type = type;
        this.toast.show = true;
      });
    },
    save() {
      const self = this;
      const L = self.options.Lang || {};
      if (!this.profile.name || !this.profile.name.trim()) {
        self.showToast(L.profileNameRequired, 'error');
        return;
      }
      // Box names are optional (blank = default "Box N"), but any custom names must
      // be unique (case-insensitive).
      const custom = this.questions.map(b => (b.name || '').trim().toLowerCase()).filter(Boolean);
      if (new Set(custom).size !== custom.length) {
        self.showToast(L.boxNameDuplicate || 'Box names must be unique.', 'error');
        return;
      }
      // A box with a money input included in the summary MUST mark a main-label
      // element — that amount needs a label to send to the PDF / email / Nezasa.
      const badBox = this.questions.findIndex(b => {
        const els = b.elements || [];
        const hasSummaryMoney = els.some(e => e.type === 'input' && e.inputType === 'money' && e.includeInSummary);
        const hasMainLabel = els.some(e => e.mainLabel);
        return hasSummaryMoney && !hasMainLabel;
      });
      if (badBox !== -1) {
        const boxName = (this.questions[badBox].name || '').trim() || ((L.box || 'Box') + ' ' + (badBox + 1));
        const msg = (L.boxMainLabelRequired || 'Pick a main label for the summary in box: {box}').replace('{box}', boxName);
        self.showToast(msg, 'error');
        return;
      }
      this.saving = true;
      const data = {
        id: this.localId || undefined,
        name: this.profile.name.trim(),
        default_lang: this.profile.default_lang || 'de',
        reservation_email_1: this.profile.reservation_email_1,
        reservation_email_2: this.profile.reservation_email_2,
        send_pdf_to_buyer: this.profile.send_pdf_to_buyer ? 1 : 0,
        allow_pdf_download: this.profile.allow_pdf_download ? 1 : 0,
        box_visibility: this.profile.box_visibility,
        languages: this.collectLanguages(),
        // Languages that should remain — the backend soft-deletes any profile
        // language row not in this list (so removed languages don't come back).
        keepLanguages: [...this.activeLangs],
        questions: this.collectQuestions(),
        user_id: this.options.user.id,
      };
      const hook = this.localId ? 'checkout-profiles/update' : 'checkout-profiles/create';
      self.doPost({ data }, hook, function (res) {
        if (res.data.status === 'error') {
          self.saving = false;
          self.showToast(L.profileSaveFailed, 'error');
          return;
        }
        // On create, adopt the returned id so the editor flips into edit mode and
        // subsequent saves update the same profile (no redirect to the list).
        const profileId = self.localId || (res.data.data && res.data.data.id);
        if (!profileId) {
          self.saving = false;
          self.showToast(L.profileSaved);
          self.$emit('saved-stay');
          return;
        }
        self.localId = profileId;
        self.doPost({ data: { id: profileId, brandingIds: self.selectedBrandingIds } }, 'checkout-profiles/set-brandings', function (res2) {
          self.saving = false;
          if (res2.data.status === 'error') {
            self.showToast(L.profileSaveFailed, 'error');
            return;
          }
          self.showToast(L.profileSaved);
          // Let the parent refresh its list in the background — it must NOT close
          // the editor (that listener is wired to keep the editor open).
          self.$emit('saved-stay');
        });
      });
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.cp-editor { padding: 0 0.5rem 2rem; }

/* Sticky action bar */
.cp-editor__bar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 0.5rem;
  background: rgba(247, 247, 247, 0.95);
  backdrop-filter: blur(4px);
  border-bottom: 1px solid #ececec;
}
.cp-editor__title { font-size: 1.5rem; font-weight: 600; margin: 0; color: #2c2c2c; }
.cp-editor__bar-actions { display: flex; gap: 0.5rem; }

.cp-editor__body { padding-top: 1.25rem; }

/* Name card */
.cp-card {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 1.5rem 1.75rem;
  margin-bottom: 1.25rem;
}
.cp-card--name {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 520px;
}

.cp-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 0.25rem;
}
.cp-input {
  width: 100%;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  padding: 0.55rem 0.75rem;
  font-size: 0.9rem;
  background: #fafafa;
  box-sizing: border-box;
  transition: border-color .15s ease, background .15s ease;
}
.cp-input:focus-within,
.cp-input:focus { outline: none; border-color: #999; background: #fff; }

/* Global controls row: step tabs + (optional) language switch */
.cp-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin: 0.5rem 0 1.25rem;
}
.cp-tab-groups {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  overflow-x: auto;
}
/* Visual divider between the checkout-step group and the standalone Brandings pill. */
.cp-tab-sep {
  width: 1px;
  align-self: stretch;
  min-height: 28px;
  background: #d9dde2;
  flex-shrink: 0;
}
.cp-lang-switch { flex-shrink: 0; }

.cp-section { animation: cp-fade .2s ease; }
@keyframes cp-fade {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.cp-hint { font-size: 0.8rem; color: #888; margin: 0; max-width: 560px; }
.cp-empty { font-style: italic; color: #999; padding: 1rem 0; }

.cp-loading { display: flex; justify-content: center; padding: 4rem; }

/* Brandings attach list */
.cp-brand-list { list-style: none; margin: 0.5rem 0 0; padding: 0; }
.cp-brand-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid #f1f1f1;
}
.cp-brand-item:last-child { border-bottom: none; }
/* Name takes the row; badge + switch trail at the right, switch always last. */
.cp-brand-name { font-size: 0.95rem; color: #2c2c2c; flex: 1; min-width: 0; }
.cp-brand-taken {
  font-size: 0.72rem;
  color: #b08400;
  background: #fff6e0;
  border-radius: 999px;
  padding: 0.15rem 0.6rem;
  flex-shrink: 0;
}
.cp-brand-switch { flex-shrink: 0; }
</style>
